/**
 * @file document-pdf.ts
 * @description Печать и просмотр PDF документа из blob (phase19.31, часть 3).
 *
 * PDF отдаёт API под cookie — простой ссылкой его не открыть, поэтому файл
 * сначала скачивается blob'ом (`downloadDocumentPdf`). Дальше:
 *  - «Печать» — PDF грузится в невидимую рамку той же страницы (адрес
 *    `blob:` — того же источника) и вызывается печать рамки; если браузер
 *    так не умеет (мобильный Safari, встроенный просмотрщик выключен) —
 *    вызывается `onFallback`: страница предлагает кнопку «Открыть PDF в
 *    новой вкладке». Сама вкладка из `onload` рамки не открывается — это не
 *    действие человека, браузер такое окно блокирует;
 *  - «Открыть» — новая вкладка с PDF; у неё обнуляется `opener`, чтобы
 *    открытая страница не могла управлять кабинетом.
 * Временные адреса освобождаются через минуту: вкладка успевает их забрать.
 */

/** Сколько живёт временный адрес файла. */
const URL_TTL_MS = 60_000;

/** Открыть файл в новой вкладке; заблокировали всплывающее окно — `false`. */
export function openBlob(blob: Blob): boolean {
  const url = URL.createObjectURL(blob);
  // Без `noopener` в параметрах: с ним `window.open` всегда отвечает `null`,
  // и блокировку окна не отличить от успеха. Связь с кабинетом рвём вручную.
  const win = window.open(url, "_blank");
  if (win) win.opener = null;
  window.setTimeout(() => URL.revokeObjectURL(url), URL_TTL_MS);
  return win !== null;
}

/**
 * Напечатать PDF через невидимую рамку. Не вышло — `onFallback`: страница
 * предлагает открыть файл во вкладке по нажатию человека (`openBlob`).
 */
export function printBlob(blob: Blob, onFallback: () => void): void {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.setAttribute("aria-hidden", "true");
  frame.src = url;
  frame.onload = () => {
    try {
      const target = frame.contentWindow;
      if (!target) throw new Error("no frame window");
      target.focus();
      target.print();
    } catch {
      onFallback();
    }
  };
  document.body.appendChild(frame);
  window.setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, URL_TTL_MS);
}

/** Размер файла словами: «84,2 КБ», «1,3 МБ». */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} КБ`;
  return `${(bytes / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} МБ`;
}
